import {msg} from "@lingui/core/macro";
import {Trans, useLingui} from "@lingui/react";
import {Link} from "react-router-dom";
import type {GroupEntry} from "../../models/GroupRepo";
import {Box, Col, css, Row} from "../../ui/Box";
import Icon from "../../ui/Icon";
import type {IconName} from "../../ui/Icon";
import {useTabSwipe} from "./use-tab-swipe";

type TabKey = "menu" | "list" | "cook";
type TabInfo = {key: TabKey; icon: IconName | null; route: string};

// only for extracting keys, will be dead-code eliminated
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const labels = [msg({id: "nav.menu"}), msg({id: "nav.list"}), msg({id: "nav.cook"})];

// in swipe order: swiping left moves one tab to the right
const tabList: TabInfo[] = [
  {key: "menu", icon: "menu", route: "/menu"},
  {key: "list", icon: null, route: ""},
  {key: "cook", icon: null, route: "/cook"},
];

const tabPath = (shortId: string, info: TabInfo) => `/g/${shortId}${info.route}`;

type TabProps = {info: TabInfo; shortId: string; current: boolean};
const Tab = ({info, current, shortId}: TabProps) => {
  const {key, icon} = info;
  const {i18n} = useLingui();
  return (
    <Link
      to={tabPath(shortId, info)}
      aria-label={icon ? i18n._(`nav.${key}`) : undefined}
      aria-current={current ? "page" : undefined}
      className={css({
        display: "flex",
        flexDir: "column",
        flex: icon ? "none" : "auto",
        align: "center",
        px: icon ? "16" : "4",
        py: "12",
      })}
    >
      <Box
        textAlign="center"
        size="12"
        textTransform="uppercase"
        bold
        relative
        color={current ? "primary" : "secondary"}
      >
        {icon ? <Icon name={icon} inline /> : <Trans id={`nav.${key}`} message={key} />}
        {current && (
          <Col absolute top="100%" left="0" right="0" align="center" pt="8">
            <Box style={{width: 12, height: 12}} rounded="full" bg="brand" />
          </Col>
        )}
      </Box>
    </Link>
  );
};

type MainNavProps = {
  current: TabKey | null;
  group: GroupEntry;
};
const GroupFooterNav = ({current, group}: MainNavProps) => {
  const idx = tabList.findIndex((info) => info.key === current);
  const prev = idx > 0 ? tabList[idx - 1] : null;
  const next = idx >= 0 && idx < tabList.length - 1 ? tabList[idx + 1] : null;
  useTabSwipe({
    prev: prev && tabPath(group.shortId, prev),
    next: next && tabPath(group.shortId, next),
  });
  return (
    <Row surface elevation="100" relative overflow="hidden">
      {tabList.map((info) => (
        <Tab key={info.key} info={info} current={current === info.key} shortId={group.shortId} />
      ))}
    </Row>
  );
};

export default GroupFooterNav;
